// Client-side mirror of backend/utils/adminOrderUtils.js computeAdminOrderTotals.
// Used only for instant wizard feedback — the server recomputes authoritatively.
import { getShippingDominantRate } from './shippingVat';

const round2 = (n) => Math.round(Number(n) * 100) / 100;
// Centime-rounded GROSS (VAT-inclusive) unit price — the SHOWN money.
const grossUp = (net, rate) => Math.round((Number(net) || 0) * (1 + (Number(rate) || 0) / 100) * 100) / 100;

// `items` may contain negative quantities: those are refund lines (money back),
// not goods — see computeAdminOrderTotals. `cartDiscountPercent` (0-100) is the
// order-wide discount the admin typed, resolved last.
export function computeAdminOrderTotals(
  items,
  shippingCost,
  isPickup,
  vatRates,
  offers,
  accountBalance = 0,
  isBusiness = false,
  cartDiscountPercent = 0
) {
  const offerDiscount = offers ? round2((offers.ruleDiscount || 0) + (offers.volumeDiscount || 0)) : 0;

  const computedItems = (items || []).map((it) => {
    const unitPrice = round2(Number(it.unit_price) || 0);
    const quantity = Number(it.quantity) || 0;
    const vatRate = Number(it.vat_rate) || 0;
    const discountPercent = Math.min(100, Math.max(0, Number(it.discount_percent) || 0));
    const net = round2(unitPrice * quantity);
    const unitPriceFinal = discountPercent >= 100 ? 0 : round2(unitPrice * (1 - discountPercent / 100));
    const afterManual = round2(unitPriceFinal * quantity);
    const discount = round2(net - afterManual);
    return {
      ...it,
      gross: net,
      discount,
      afterManual,
      vat_rate: vatRate,
      // unit_price_final = agreed base minus ONLY the per-line manual discount.
      unit_price_final: unitPriceFinal,
      credit: quantity < 0,
    };
  });

  const goods = computedItems.filter((i) => !i.credit);
  const creditLines = computedItems.filter((i) => i.credit);

  const proRataBase = goods.reduce((s, i) => s + i.afterManual, 0);
  goods.forEach((i) => {
    const share = proRataBase > 0 ? i.afterManual / proRataBase : 0;
    i.share = share;
    i.offerDiscount = round2(offerDiscount * share);
  });

  const freeLines = (offers?.freeItems || []).map((fi) => ({
    name: fi.name || null,
    sku: fi.sku || null,
    quantity: fi.quantity || 1,
    unit_price: 0,
    vat_rate: Number(fi.vat_rate) || 0,
    gross: 0,
    discount: 0,
    offerDiscount: 0,
    afterManual: 0,
    finalNet: 0,
    unit_price_final: 0,
    credit: false,
  }));

  const allItems = goods.concat(freeLines);
  const allLines = allItems.concat(creditLines);

  const grossSubtotal = round2(goods.reduce((s, i) => s + i.gross, 0));
  // subtotal = post-manual item net (Σ unit_price_final × qty) — storage.
  const subtotal = round2(allItems.reduce((s, i) => s + i.afterManual, 0));
  const manualDiscountTotal = round2(goods.reduce((s, i) => s + i.discount, 0));

  // Order-wide admin discount, resolved LAST on what is still owed. It lowers the
  // discountedSubtotal the shipping tiers are evaluated on, exactly like the other
  // cart-level discounts do.
  const cartPct = Math.min(100, Math.max(0, Number(cartDiscountPercent) || 0));
  const cartBase = Math.max(0, round2(subtotal - offerDiscount));
  const cartDiscountNet = cartPct > 0 ? round2(cartBase * (cartPct / 100)) : 0;
  const discountedSubtotal = Math.max(0, round2(subtotal - offerDiscount - cartDiscountNet));

  // The cart discount lands on the goods lines too, so the VAT-exclusive view
  // gives back the VAT it saved (otherwise the total carries too much VAT).
  goods.forEach((i) => {
    i.cartDiscount = round2(cartDiscountNet * i.share);
    i.finalNet = round2(i.afterManual - i.offerDiscount - i.cartDiscount);
  });

  // Money back: credit only — no stock, no fulfillment, and its own VAT rate
  // rather than a share of the cart discounts.
  const creditNet = round2(creditLines.reduce((s, i) => s + (i.afterManual || 0), 0));

  const shippingFree = offers?.shippingFree || false;
  const effectiveShipping = (!isPickup && !shippingFree) ? shippingCost : 0;
  const netShipping = round2(effectiveShipping);

  const vatExclusive = !!isBusiness;
  let vatAmount;
  let itemVat;
  let creditVat = 0;
  let vatOnDiscount;
  let totalBeforeRounding;

  if (vatExclusive) {
    // BUSINESS: VAT = final discounted net × rate/100 per line; shipping VAT at
    // the dominant rate. Total = post-manual net − offer/volume/cart + NET
    // shipping + credit + VAT.
    allItems.forEach((i) => {
      i.vat = i.vat_rate > 0 ? round2((i.finalNet || 0) * (i.vat_rate / 100)) : 0;
    });
    itemVat = round2(allItems.reduce((s, i) => s + i.vat, 0));
    const shippingVat = effectiveShipping > 0
      ? round2(effectiveShipping * (getShippingDominantRate(allItems.map((i) => ({ net: i.finalNet || 0, vat_rate: i.vat_rate }))) / 100))
      : 0;
    creditVat = round2(creditLines.reduce(
      (s, i) => s + (i.vat_rate > 0 ? round2((i.afterManual || 0) * (i.vat_rate / 100)) : 0), 0));
    vatAmount = round2(itemVat + shippingVat + creditVat);
    let preDiscountItemVat = 0;
    for (const item of goods) {
      const r = item.vat_rate;
      if (r > 0 && item.afterManual > 0) preDiscountItemVat += item.afterManual * (r / 100);
    }
    vatOnDiscount = round2(round2(preDiscountItemVat) - itemVat);
    totalBeforeRounding = round2(
      subtotal - offerDiscount - cartDiscountNet + creditNet + effectiveShipping + vatAmount);
  } else {
    // RETAIL: money-of-record is the SHOWN centime-rounded GROSS unit prices.
    const dominantRate = getShippingDominantRate(allItems.map((i) => ({ net: i.afterManual, vat_rate: i.vat_rate })));
    const gf = 1 + dominantRate / 100;
    const money = (net) => round2((Number(net) || 0) * gf);
    const shownItemMoney = allItems.reduce((s, i) => s + grossUp(i.unit_price_final || 0, i.vat_rate) * (i.quantity || 0), 0);
    const netItemMoney = round2(allItems.reduce((s, i) => s + (i.afterManual || 0), 0));
    itemVat = round2(Math.max(0, shownItemMoney - netItemMoney));
    const shownShipping = money(effectiveShipping);
    const shippingVat = effectiveShipping > 0 ? round2(Math.max(0, shownShipping - effectiveShipping)) : 0;
    // Shown gross of the money-back lines: centime-rounded gross at each credit
    // line's own VAT rate, mirroring how the goods lines are shown.
    const shownCredits = round2(creditLines.reduce(
      (s, i) => s + grossUp(i.unit_price_final || 0, i.vat_rate) * (i.quantity || 0), 0));
    creditVat = round2(shownCredits - creditNet);
    vatAmount = round2(itemVat + shippingVat + creditVat);
    vatOnDiscount = 0;
    const ruleMoney = money(offers?.ruleDiscount || 0);
    const volumeMoney = money(offers?.volumeDiscount || 0);
    const cartMoney = money(cartDiscountNet);
    totalBeforeRounding = round2(
      shownItemMoney - ruleMoney - volumeMoney - cartMoney + shownShipping + shownCredits);
  }

  // Applied balance (mirror of the backend checkout/order-balance rule).
  const bal = Number(accountBalance) || 0;
  let appliedBalance = 0;
  let payableBeforeRound = totalBeforeRounding;
  if (bal > 0) {
    if (bal >= totalBeforeRounding) {
      appliedBalance = totalBeforeRounding;
      payableBeforeRound = 0;
    } else {
      appliedBalance = bal;
      payableBeforeRound = round2(totalBeforeRounding - appliedBalance);
    }
  } else if (bal < 0) {
    appliedBalance = bal;
    payableBeforeRound = round2(totalBeforeRounding - appliedBalance);
  }
  const rawTotal = Math.max(0, payableBeforeRound);
  const roundedTotal = Math.round(rawTotal * 20) / 20;
  const roundOffDiff = round2(roundedTotal - rawTotal);
  // Refunds can outweigh what is owed; the server rejects those orders instead of
  // issuing an invoice that does not add up, so the wizard flags it up front.
  const creditsExceedTotal = payableBeforeRound < 0;

  // Display helpers: the portal shows retail GROSS and business NET, so every
  // NET cart-level amount is grossed at the order's dominant rate for retail.
  const dominantFactor = 1 + getShippingDominantRate(
    allItems.map((i) => ({ net: i.afterManual, vat_rate: i.vat_rate }))) / 100;
  const displayed = (net) => (vatExclusive ? round2(net) : round2(net * dominantFactor));
  // Retail's money-of-record is the SUM OF SHOWN unit prices (each centime-rounded
  // at its own rate), not the subtotal grossed at one dominant rate — the two differ
  // by a few cents, and the rows must reconcile against the total.
  const shownGoods = round2(allItems.reduce((s, i) => s + grossUp(i.unit_price_final || 0, i.vat_rate) * (i.quantity || 0), 0));
  const shownCredits = vatExclusive
    ? creditNet
    : round2(creditLines.reduce((s, i) => s + grossUp(i.unit_price_final || 0, i.vat_rate) * (i.quantity || 0), 0));

  return {
    items: allLines,
    creditItems: creditLines,
    subtotal,
    discountedSubtotal,
    grossSubtotal,
    manualDiscount: round2(manualDiscountTotal),
    totalItemDiscount: round2(manualDiscountTotal + offerDiscount + cartDiscountNet),
    cartDiscountPercent: cartPct,
    cartDiscountNet,
    creditNet,
    creditVat,
    ruleDiscount: round2(offers?.ruleDiscount || 0),
    volumeDiscount: round2(offers?.volumeDiscount || 0),
    volumeDiscountPercentage: offers?.volumeDiscountPercentage ?? null,
    shippingFree,
    freeItems: offers?.freeItems || [],
    vatAmount,
    shippingCost: isPickup ? 0 : effectiveShipping,
    hasShippingFree: isPickup ? false : effectiveShipping === 0,
    isPickup,
    totalBeforeRounding,
    creditsExceedTotal,
    appliedBalance,
    payableBeforeRound: rawTotal,
    roundedTotal,
    roundOffDiff,
    vatExclusive,
    netSubtotal: round2(subtotal),
    netShipping,
    vatOnDiscount,
    // Per-type display helpers for the wizard summary (mirrors how the portal
    // renders retail gross vs business net rows). The subtotal covers GOODS only:
    // refunds get their own row, exactly as the invoice and the order emails lay
    // it out, so the summary rows add up to the payable.
    displaySubtotal: vatExclusive ? round2(subtotal) : shownGoods,
    displayCartDiscount: displayed(cartDiscountNet),
    displayRefund: vatExclusive ? creditNet : shownCredits,
    displayRule: displayed(offers?.ruleDiscount || 0),
    displayVolume: displayed(offers?.volumeDiscount || 0),
    displayShipping: displayed(effectiveShipping),
    showVatRow: vatExclusive,
  };
}

export function getShippingCostForSubtotal(subtotal, tiers, fulfillmentMethod) {
  if (fulfillmentMethod !== 'delivery') return 0;
  const sorted = [...(tiers || [])].sort((a, b) => Number(b.minSubtotal) - Number(a.minSubtotal));
  const tier = sorted.find((t) => subtotal >= Number(t.minSubtotal));
  return tier ? Number(tier.cost) : 0;
}

export function todayIso() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

export function plusDaysIso(dateString, days) {
  const d = new Date(`${dateString}T12:00:00`);
  d.setDate(d.getDate() + days);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Inverse of plusDaysIso: the whole days from one ISO date to another, or null when
// either input is not an ISO date. Needed because due_days is transient — only
// orders.due_date is persisted — so the wizard has to recover the day count when it
// loads a stored order. Both ends are parsed as UTC midnight so a DST shift cannot
// round the count to the wrong day.
export function daysBetweenIso(fromIso, toIso) {
  if (!ISO_DATE_RE.test(fromIso || '') || !ISO_DATE_RE.test(toIso || '')) return null;
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86400000);
}